<template>
  <div class="organizations-page">
    <div class="organizations-hero">
      <div class="hero-copy">
        <p class="hero-eyebrow">Organization Oversight</p>
        <h1>NGO management.</h1>
        <p class="hero-description">
          Review incomplete and pending registrations, inspect approved organizations, and move
          through operational decisions from one elevated admin view.
        </p>
      </div>

      <div class="hero-stats">
        <div class="stat-card">
          <span class="stat-label">Active view</span>
          <strong class="stat-value">{{ activeTabLabel }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">Results</span>
          <strong class="stat-value">{{ filteredOrganizations.length }}</strong>
        </div>
      </div>
    </div>

    <div class="admin-nav">
      <ul class="nav">
        <li><a href="#">Home</a></li>
        <li><a href="#">Organizations</a></li>
        <li><a href="#" aria-current="page">All NGOs</a></li>
      </ul>
    </div>

    <section class="organization-wrapper">
      <div class="section-heading">
        <div>
          <p class="section-kicker">Directory</p>
          <h2>See All NGOs</h2>
        </div>
      </div>

      <div class="tab-switcher" role="tablist" aria-label="Organization status tabs">
        <button
          type="button"
          class="tab-button"
          :class="{ active: activeTab === 'Incomplete' }"
          @click="switchToPendingTab('Incomplete')"
        >
          <span class="tab-title">Incomplete NGOs</span>
          <span class="tab-count">{{ incompleteCount }}</span>
        </button>
        <button
          type="button"
          class="tab-button"
          :class="{ active: activeTab === 'Pending' }"
          @click="switchToPendingTab('Pending')"
        >
          <span class="tab-title">Pending NGOs</span>
          <span class="tab-count">{{ pendingCount }}</span>
        </button>
        <button
          type="button"
          class="tab-button"
          :class="{ active: activeTab === 'Approved' }"
          @click="switchToApprovedTab('Approved')"
        >
          <span class="tab-title">Approved NGOs</span>
          <span class="tab-count">{{ approvedCount }}</span>
        </button>
      </div>

      <div class="filters-panel">
        <div class="filters-intro">
          <p class="filters-label">Refine results</p>
          <h3>Search and narrow the organization list</h3>
        </div>

        <div class="filters-grid">
          <div class="search-shell">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              v-model="searchParams"
              class="search_input"
              type="search"
              placeholder="Search organizations by name"
              aria-label="Search organizations"
            />
          </div>

          <div class="select-shell">
            <base-select
              height="3.35rem"
              width="100%"
              :items="registrationYearOptions"
              v-model="selectedRegistrationYear"
              label="Filter by Registration Year"
            />
          </div>
        </div>
      </div>

      <div v-if="organizations.length === 0 && isBusy" class="feedback-state">
        <div class="spinner-border load-icon" role="status"></div>
        <p>Loading organizations...</p>
      </div>

      <div
        v-else-if="filteredOrganizations.length === 0"
        class="feedback-state feedback-state--empty"
      >
        <h3>No organizations found</h3>
        <p>Try another search term or change the registration year filter to widen the results.</p>
      </div>

      <div v-else-if="activeTab !== 'Approved'" class="table-shell">
        <div class="table-shell__header">
          <div>
            <p class="table-kicker">
              {{
                activeTab === 'Incomplete' ? 'Incomplete organizations' : 'Pending organizations'
              }}
            </p>
            <h3>Registration review queue</h3>
          </div>
        </div>

        <div class="table-scroll-hint">Swipe left or right to view more columns</div>

        <div class="table-scroll-shell">
          <b-table
            id="pending-organizations-table"
            thead-class="custom-thead"
            tbody-tr-class="table-row"
            :hover="false"
            :bordered="false"
            :items="filteredOrganizations"
            :fields="pendingFields"
            :busy="isBusy"
            :current-page="currentPage"
            :per-page="perPage"
            primary-key="id"
            show-empty
            class="premium-table"
          >
            <template #empty="scope">
              <h4 class="empty-table-text">{{ scope.emptyText }}</h4>
            </template>

            <template #table-busy>
              <div class="text-center text-success my-4">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Loading...</strong>
              </div>
            </template>

            <template #cell(id)="data">
              <span class="index-pill">{{ getRowNumber(data.index) }}</span>
            </template>

            <template #cell(name)="data">
              <span class="cell-name">
                {{ data.value || '-' }}
              </span>
            </template>

            <template #cell(location)="data">
              <span>{{ getOrganizationLocation(data.item) }}</span>
            </template>

            <template #cell(cac_registration_year)="data">
              <span>{{ data.value || '-' }}</span>
            </template>

            <template #cell(email)="data">
              <span class="email-cell" :title="getOrganizationEmail(data.item) || '-'">
                {{ getOrganizationEmail(data.item) || '-' }}
              </span>
            </template>

            <template #cell(contact)="data">
              <div class="contact-stack">
                <span>{{ getOrganizationPhone(data.item) || '-' }}</span>
              </div>
            </template>

            <template #cell(registered_on)="data">
              <span>{{ formatDate(data.value) }}</span>
            </template>

            <template #cell(action)="data">
              <button
                type="button"
                class="table-link-btn"
                @click="navigateToPendingDetails(data.item)"
              >
                View Details
              </button>
            </template>

            <template #head(action)>
              <p></p>
            </template>

            <template #head()="data">
              <span class="table-header-style">
                {{ data.label }}
              </span>
            </template>
          </b-table>
        </div>

        <div class="table-footer">
          <div class="pagination-meta">
            <p>Rows per page</p>
            <select id="pending-zone" v-model.number="perPage" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
            </select>
          </div>

          <p class="pagination-range">
            {{ startRow }} - {{ endRow }} of {{ filteredOrganizations.length }}
          </p>

          <div class="pagination-shell">
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredOrganizations.length"
              :per-page="perPage"
              aria-controls="pending-organizations-table"
              first-number
              last-number
              pills
              size="sm"
              class="premium-pagination"
            >
              <template #prev-text>
                <span class="pagination-nav-label">Prev</span>
              </template>
              <template #next-text>
                <span class="pagination-nav-label">Next</span>
              </template>
            </b-pagination>
          </div>
        </div>
      </div>

      <div v-else class="table-shell">
        <div class="table-shell__header">
          <div>
            <p class="table-kicker">Approved organizations</p>
            <h3>Detailed operational register</h3>
          </div>
        </div>

        <div class="table-scroll-hint">Swipe left or right to view more columns</div>

        <div class="table-scroll-shell">
          <b-table
            id="approved-organizations-table"
            thead-class="custom-thead"
            tbody-tr-class="table-row"
            :hover="false"
            :bordered="false"
            :items="filteredOrganizations"
            :fields="fields"
            :busy="isBusy"
            :current-page="currentPage"
            :per-page="perPage"
            primary-key="id"
            show-empty
            ref="table"
            class="premium-table"
          >
            <template #empty="scope">
              <h4 class="empty-table-text">{{ scope.emptyText }}</h4>
            </template>

            <template #table-busy>
              <div class="text-center text-success my-4">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Loading...</strong>
              </div>
            </template>

            <template #cell(action)="data">
              <div class="d-flex drop-wrap">
                <button @click="toggleDropdownById(data.item.id)" class="table-action-btn">
                  <i class="fas fa-ellipsis-h"></i>
                </button>

                <div
                  v-if="data.item.showDrop === true"
                  class="drop-body"
                  @mouseleave="closeDropdown"
                >
                  <p @click="navigateToEdit(data.item)">View Details</p>
                  <p @click="openStatusModal(data.item)">
                    {{ data.item.active ? 'Suspend NGO' : 'Activate NGO' }}
                  </p>
                  <p v-if="!data.item.active" @click="openNotifyModal(data.item)">Notify NGO</p>
                  <p @click="openDeleteDocumentsModal(data.item)">Delete Documents</p>
                  <p @click="openDeleteModal(data.item)">Delete NGO</p>
                </div>
              </div>
            </template>

            <template #cell(id)="data">
              <span class="index-pill">{{ getRowNumber(data.index) }}</span>
            </template>

            <template #head(action)>
              <p></p>
            </template>

            <template #head()="data">
              <span class="table-header-style">
                {{ data.label }}
              </span>
            </template>

            <template #cell(name)="data">
              <span class="cell-name">
                {{ data.value }}
              </span>
            </template>

            <template #cell(location)="data">
              <span>{{ getOrganizationLocation(data.item) }}</span>
            </template>

            <template #cell(email)="data">
              <span class="email-cell" :title="getOrganizationEmail(data.item) || '-'">
                {{ getOrganizationEmail(data.item) || '-' }}
              </span>
            </template>

            <template #cell(contact)="data">
              <div class="contact-stack">
                <span>{{ getOrganizationPhone(data.item) || '-' }}</span>
              </div>
            </template>

            <template #cell(subscription_status)="data">
              <span class="status-badge" :class="getSubscriptionStatusClass(data.value)">
                {{ formatSubscriptionStatus(data.value) }}
              </span>
            </template>

            <template #head(id)="data">
              <span class="table-header-style">{{ data.label }}</span>
            </template>
          </b-table>
        </div>

        <div class="table-footer">
          <div class="pagination-meta">
            <p>Rows per page</p>
            <select id="zone" v-model.number="perPage" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
            </select>
          </div>

          <p class="pagination-range">
            {{ startRow }} - {{ endRow }} of {{ filteredOrganizations.length }}
          </p>

          <div class="pagination-shell">
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredOrganizations.length"
              :per-page="perPage"
              aria-controls="approved-organizations-table"
              first-number
              last-number
              pills
              size="sm"
              class="premium-pagination"
            >
              <template #prev-text>
                <span class="pagination-nav-label">Prev</span>
              </template>
              <template #next-text>
                <span class="pagination-nav-label">Next</span>
              </template>
            </b-pagination>
          </div>
        </div>
      </div>

      <OrganizationActionModal
        :visible="showStatusModal && !!pendingStatusOrganization"
        :loading="isUpdatingStatus"
        :kicker="statusModalKicker"
        :title="statusModalTitle"
        :description="statusModalDescription"
        :confirm-text="statusModalConfirmText"
        :confirm-variant="statusModalConfirmVariant"
        @close="closeStatusModal"
        @confirm="confirmStatusUpdate"
      />

      <OrganizationActionModal
        :visible="showDeleteModal && !!pendingDeleteOrganization"
        :loading="isDeleting"
        kicker="Delete"
        title="Delete this organization?"
        :description="
          pendingDeleteOrganization && pendingDeleteOrganization.name
            ? `This will permanently remove ${pendingDeleteOrganization.name}.`
            : 'This will permanently remove this organization.'
        "
        confirm-text="Delete Organization"
        confirm-variant="danger"
        @close="closeDeleteModal"
        @confirm="confirmDeleteOrganization"
      />

      <OrganizationActionModal
        :visible="showDeleteDocumentsModal && !!pendingDocumentOrganization"
        :loading="isDeletingDocuments"
        kicker="Documents"
        title="Delete uploaded documents?"
        :description="
          pendingDocumentOrganization && pendingDocumentOrganization.name
            ? `This will remove uploaded documents for ${pendingDocumentOrganization.name}.`
            : 'This will remove the uploaded organization documents.'
        "
        confirm-text="Delete Documents"
        confirm-variant="danger"
        @close="closeDeleteDocumentsModal"
        @confirm="confirmDeleteDocuments"
      />

      <OrganizationActionModal
        :visible="showNotifyModal && !!pendingNotifyOrganization"
        :loading="isNotifying"
        kicker="Renewal"
        title="Notify this organization to renew?"
        :description="
          pendingNotifyOrganization && pendingNotifyOrganization.name
            ? `Send a renewal reminder to ${pendingNotifyOrganization.name}.`
            : 'Send a renewal reminder to this organization.'
        "
        confirm-text="Notify Organization"
        confirm-variant="success"
        @close="closeNotifyModal"
        @confirm="confirmNotifyOrganization"
      />
    </section>
  </div>
</template>

<script>
import { createNotification } from '../../config/api-modules/api.notifications';
import {
  activateOrganization,
  deleteOrganization,
  deleteOrganizationDocuments,
  getOrganizationByQuery,
  getProgramArea,
  suspendOrganization,
} from '../../../ngo/config/api-modules/api.organization';
import OrganizationActionModal from './modals/OrganizationActionModal.vue';

export default {
  components: {
    OrganizationActionModal,
  },
  data() {
    return {
      location: [
        {
          name: 'All',
          states: ['All'],
        },
        {
          name: 'North Central',
          states: ['Benue', 'FCT', 'Kogi', 'Kwara', 'Nasarawa', 'Niger', 'Plateau'],
        },
        { name: 'North East', states: ['Adamawa', 'Bauchi', 'Borno', 'Gombe', 'Taraba', 'Yobe'] },
        {
          name: 'North West',
          states: ['Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Sokoto', 'Zamfara'],
        },
        { name: 'South East', states: ['Abia', 'Anambra', 'Ebonyi', 'Enugu', 'Imo'] },
        {
          name: 'South South',
          states: ['Akwa Ibom', 'Bayelsa', 'Cross River', 'Delta', 'Edo', 'Rivers'],
        },
        { name: 'South West', states: ['Ekiti', 'Lagos', 'Ogun', 'Ondo', 'Osun', 'Oyo'] },
      ],
      zones: [
        'All',
        'North Central',
        'North East',
        'North West',
        'South East',
        'South South',
        'South West',
      ],
      states: [
        'All',
        'Niger',
        'Kogi',
        'Benue',
        'Plateau',
        'Nasarawa',
        'Kwara',
        'FCT',
        'Bauchi',
        'Borno',
        'Taraba',
        'Adamawa',
        'Gombe',
        'Yobe',
        'Zamfara',
        'Sokoto',
        'Kaduna',
        'Kebbi',
        'Katsina',
        'Kano',
        'Jigawa',
        'Enugu',
        'Imo',
        'Ebonyi',
        'Abia',
        'Anambra',
        'Bayelsa',
        'Akwa Ibom',
        'Edo',
        'Rivers',
        'Cross River',
        'Delta',
        'Oyo',
        'Ekiti',
        'Osun',
        'Ondo',
        'Lagos',
        'Ogun',
      ],
      organizationType: [
        'All',
        'International Partner',
        'Non governmental Organization',
        'Unspecified',
      ],
      filters: {
        state: 'All',
        type: 'All',
        geo_zone: 'All',
        areas_of_focus: {
          id: 'All',
          name: 'All',
          description: '0.0',
        },
      },
      searchParams: '',
      programs: [],
      allOrganizations: [],
      organizations: [],
      activeTab: 'Pending',
      perPage: 20,
      currentPage: 1,
      isBusy: false,
      fields: [
        { key: 'id', label: 'S/N', sortable: false },
        { key: 'name', label: 'NAME OF NGO', sortable: true },
        { key: 'cac_registration_year', label: 'YEAR OF REG.', sortable: true },
        { key: 'location', label: 'LOCATION', sortable: false },
        { key: 'email', label: 'EMAIL', sortable: false },
        { key: 'contact', label: 'PHONE', sortable: false },
        { key: 'subscription_status', label: 'STATUS', sortable: true },
        { key: 'action', label: 'ACTION' },
      ],
      pendingFields: [
        { key: 'id', label: '#' },
        { key: 'name', label: 'Organization Name' },
        { key: 'location', label: 'Location' },
        { key: 'email', label: 'Email' },
        { key: 'contact', label: 'Phone' },
        { key: 'registered_on', label: 'Date Registered' },
        { key: 'action', label: '' },
      ],
      items: [],
      previewData: null,
      debounceTimeout: null,
      selectedRegistrationYear: 'All Years',
      showStatusModal: false,
      isUpdatingStatus: false,
      pendingStatusOrganization: null,
      showDeleteModal: false,
      isDeleting: false,
      pendingDeleteOrganization: null,
      showDeleteDocumentsModal: false,
      isDeletingDocuments: false,
      pendingDocumentOrganization: null,
      showNotifyModal: false,
      isNotifying: false,
      pendingNotifyOrganization: null,
    };
  },
  computed: {
    activeTabLabel() {
      return this.activeTab;
    },
    startRow() {
      if (this.filteredOrganizations.length === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    endRow() {
      return Math.min(this.currentPage * this.perPage, this.filteredOrganizations.length);
    },
    filteredStates() {
      const zone = this.location.find((l) => l.name === this.filters.geo_zone);
      return zone ? zone.states : [];
    },
    filteredOrganizations() {
      let result = [...this.organizations];

      if (this.selectedRegistrationYear && this.selectedRegistrationYear !== 'All Years') {
        result = result.filter((org) =>
          this.matchesSelectedRegistrationYear(org.cac_registration_year)
        );
      }

      if (this.searchParams.trim() !== '') {
        const searchTerm = this.searchParams.trim().toLowerCase();
        result = result.filter((org) => org.name?.toLowerCase().includes(searchTerm));
      }

      result = result.sort((a, b) => {
        if (this.activeTab === 'Approved') {
          const yearA = Number.parseInt(a.cac_registration_year, 10);
          const yearB = Number.parseInt(b.cac_registration_year, 10);
          const hasValidYearA = Number.isFinite(yearA);
          const hasValidYearB = Number.isFinite(yearB);

          if (!hasValidYearA && !hasValidYearB) return 0;
          if (!hasValidYearA) return 1;
          if (!hasValidYearB) return -1;

          return yearB - yearA;
        }

        const dateA = a.registered_on ? new Date(a.registered_on) : null;
        const dateB = b.registered_on ? new Date(b.registered_on) : null;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB - dateA;
      });

      return result;
    },
    registrationYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];

      for (let year = currentYear; year >= 2000; year -= 1) {
        years.push(String(year));
      }

      return ['All Years', ...years];
    },
    pendingCount() {
      return this.allOrganizations.filter((item) => this.getOrganizationTab(item) === 'Pending')
        .length;
    },
    incompleteCount() {
      return this.allOrganizations.filter((item) => this.getOrganizationTab(item) === 'Incomplete')
        .length;
    },
    approvedCount() {
      return this.allOrganizations.filter((item) => this.getOrganizationTab(item) === 'Approved')
        .length;
    },
    statusModalKicker() {
      return this.pendingStatusOrganization && this.pendingStatusOrganization.active
        ? 'Suspend'
        : 'Activate';
    },
    statusModalTitle() {
      return this.pendingStatusOrganization && this.pendingStatusOrganization.active
        ? 'Suspend this organization?'
        : 'Activate this organization?';
    },
    statusModalDescription() {
      const organizationName =
        (this.pendingStatusOrganization && this.pendingStatusOrganization.name) ||
        'this organization';

      return this.pendingStatusOrganization && this.pendingStatusOrganization.active
        ? `This will mark ${organizationName} as inactive.`
        : `This will mark ${organizationName} as active.`;
    },
    statusModalConfirmText() {
      return this.pendingStatusOrganization && this.pendingStatusOrganization.active
        ? 'Suspend Organization'
        : 'Activate Organization';
    },
    statusModalConfirmVariant() {
      return this.pendingStatusOrganization && this.pendingStatusOrganization.active
        ? 'danger'
        : 'success';
    },
  },
  watch: {
    activeTab() {
      this.currentPage = 1;
    },
    filteredOrganizations() {
      this.ensureValidCurrentPage();
    },
    searchParams() {
      this.currentPage = 1;
    },
    selectedRegistrationYear() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
  },
  methods: {
    getOrganizationTab(organization) {
      if (
        String(organization.status || '')
          .trim()
          .toLowerCase() === 'approved'
      )
        return 'Approved';
      return String(organization.name || '').trim() &&
        String(this.getOrganizationEmail(organization)).trim()
        ? 'Pending'
        : 'Incomplete';
    },
    async getPrograms() {
      try {
        const { data } = await getProgramArea();
        this.programs = [
          {
            id: 'All',
            name: 'All',
            description: '0.0',
          },
          ...data.results,
        ];
      } catch {
        this.programs = [];
      }
    },
    normalizeOrganizations(results = []) {
      return results.map((item) => ({
        ...item,
        showDrop: false,
      }));
    },
    applyActiveTabFilter() {
      this.organizations = this.allOrganizations
        .filter((item) => this.getOrganizationTab(item) === this.activeTab)
        .map((item) => ({
          ...item,
          showDrop: item.showDrop || false,
        }));
    },
    async getOrganization() {
      this.isBusy = true;
      const temp = { ...this.filters };
      this.organizations = [];

      try {
        const { data } = await getOrganizationByQuery(temp);
        const results = data?.data?.results ?? [];
        this.items = results;
        this.allOrganizations = this.normalizeOrganizations(results);
        this.applyActiveTabFilter();
      } catch {
        this.items = [];
        this.allOrganizations = [];
        this.organizations = [];
      } finally {
        this.isBusy = false;
      }
    },
    async getPendingOrganization() {
      this.activeTab = 'Pending';
      this.applyActiveTabFilter();
    },
    async getApprovedOrganization() {
      this.activeTab = 'Approved';
      this.applyActiveTabFilter();
    },
    switchActiveTab(tab) {
      this.activeTab = tab;
      this.applyActiveTabFilter();
    },
    switchToPendingTab(tab) {
      this.activeTab = tab;
      this.applyActiveTabFilter();
    },
    switchToApprovedTab(tab) {
      this.activeTab = tab;
      this.applyActiveTabFilter();
    },
    navigateToEdit(item) {
      localStorage.setItem('from', 'Approved');
      this.$router.push(`/admin/organization/${item.id}`);
    },
    navigateToPendingDetails(item) {
      localStorage.setItem('from', 'Pending');
      this.$router.push(`/admin/pending-organization/${item.id}`);
    },
    formatDate(isoString) {
      if (!isoString) return '-';

      const date = new Date(isoString);

      if (Number.isNaN(date.getTime())) return '-';

      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    getOrganizationLocation(organization) {
      if (organization.state && organization.geo_zone) {
        return `${organization.state} • ${organization.geo_zone}`;
      }

      return organization.state || organization.geo_zone || '-';
    },
    getOrganizationEmail(organization) {
      return organization.email || organization.contact_email || organization.user?.email || '';
    },
    getOrganizationPhone(organization) {
      return (
        organization.phone_number || organization.contact_phone || organization.contact_number || ''
      );
    },
    getRowNumber(index) {
      return (this.currentPage - 1) * this.perPage + index + 1;
    },
    formatSubscriptionStatus(status) {
      if (!status) return 'unknown';
      return String(status).replace(/_/g, ' ').toLowerCase();
    },
    getSubscriptionStatusClass(status) {
      const normalizedStatus = String(status || '').toLowerCase();

      if (['active', 'paid', 'current', 'subscribed'].includes(normalizedStatus)) {
        return 'status-badge--active';
      }

      if (['inactive', 'expired', 'unpaid', 'cancelled', 'canceled'].includes(normalizedStatus)) {
        return 'status-badge--inactive';
      }

      return 'status-badge--neutral';
    },
    openStatusModal(item) {
      this.pendingStatusOrganization = item;
      this.showStatusModal = true;
      this.closeDropdown();
    },
    closeStatusModal(force = false) {
      if (this.isUpdatingStatus && !force) {
        return;
      }

      this.showStatusModal = false;
      this.pendingStatusOrganization = null;
    },
    openDeleteModal(item) {
      this.pendingDeleteOrganization = item;
      this.showDeleteModal = true;
      this.closeDropdown();
    },
    openDeleteDocumentsModal(item) {
      this.pendingDocumentOrganization = item;
      this.showDeleteDocumentsModal = true;
      this.closeDropdown();
    },
    openNotifyModal(item) {
      if (item.active) {
        return;
      }

      this.pendingNotifyOrganization = item;
      this.showNotifyModal = true;
      this.closeDropdown();
    },
    closeDeleteModal(force = false) {
      if (this.isDeleting && !force) {
        return;
      }

      this.showDeleteModal = false;
      this.pendingDeleteOrganization = null;
    },
    closeDeleteDocumentsModal(force = false) {
      if (this.isDeletingDocuments && !force) {
        return;
      }

      this.showDeleteDocumentsModal = false;
      this.pendingDocumentOrganization = null;
    },
    closeNotifyModal(force = false) {
      if (this.isNotifying && !force) {
        return;
      }

      this.showNotifyModal = false;
      this.pendingNotifyOrganization = null;
    },
    updateOrganizationStatusLocally(id, active) {
      this.allOrganizations = this.allOrganizations.map((organization) =>
        organization.id === id ? { ...organization, active, showDrop: false } : organization
      );
      this.applyActiveTabFilter();
      this.items = this.items.map((item) => (item.id === id ? { ...item, active } : item));
    },
    clearOrganizationDocumentsLocally(id) {
      const clearedDocumentFields = {
        umbrella_org_file: null,
        umbrella_org_file_url: null,
        cac_certificate_file: null,
        cac_certificate_file_url: null,
        contact_person_cv_file: null,
        contact_person_cv_file_url: null,
        activities_documentation_file: null,
        activities_documentation_file_url: null,
        letter_of_introduction: null,
        letter_of_introduction_url: null,
        file_logo: null,
        file_logo_url: null,
      };

      this.allOrganizations = this.allOrganizations.map((organization) =>
        organization.id === id
          ? { ...organization, ...clearedDocumentFields, showDrop: false }
          : organization
      );
      this.applyActiveTabFilter();
      this.items = this.items.map((item) =>
        item.id === id ? { ...item, ...clearedDocumentFields } : item
      );
    },
    removeOrganizationLocally(id) {
      this.allOrganizations = this.allOrganizations.filter(
        (organization) => organization.id !== id
      );
      this.applyActiveTabFilter();
      this.items = this.items.filter((item) => item.id !== id);
    },
    ensureValidCurrentPage() {
      const totalRows = this.filteredOrganizations.length;

      if (totalRows === 0) {
        this.currentPage = 1;
        return;
      }

      const totalPages = Math.ceil(totalRows / this.perPage);

      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
    },
    matchesSelectedRegistrationYear(registrationYear) {
      if (!registrationYear) {
        return false;
      }

      const normalizedYear = String(registrationYear).trim();

      if (!/^\d{4}$/.test(normalizedYear)) {
        return false;
      }

      return normalizedYear === this.selectedRegistrationYear;
    },
    async confirmStatusUpdate() {
      if (!this.pendingStatusOrganization?.id) {
        return;
      }

      this.isUpdatingStatus = true;
      const nextActiveState = !this.pendingStatusOrganization.active;

      try {
        if (nextActiveState) {
          await activateOrganization(this.pendingStatusOrganization.id);
        } else {
          await suspendOrganization(this.pendingStatusOrganization.id);
        }
        this.updateOrganizationStatusLocally(this.pendingStatusOrganization.id, nextActiveState);
        this.$toast.show({
          type: 'SUCCESS',
          message: nextActiveState
            ? 'Organization activated successfully'
            : 'Organization suspended successfully',
        });
        this.closeStatusModal(true);
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to update organization status',
        });
      } finally {
        this.isUpdatingStatus = false;
      }
    },
    async confirmDeleteOrganization() {
      if (!this.pendingDeleteOrganization?.id) {
        return;
      }

      this.isDeleting = true;

      try {
        await deleteOrganization(this.pendingDeleteOrganization.id);
        this.removeOrganizationLocally(this.pendingDeleteOrganization.id);
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Organization deleted successfully',
        });
        this.closeDeleteModal(true);
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to delete organization',
        });
      } finally {
        this.isDeleting = false;
      }
    },
    async confirmDeleteDocuments() {
      if (!this.pendingDocumentOrganization?.id) {
        return;
      }

      this.isDeletingDocuments = true;

      try {
        await deleteOrganizationDocuments(this.pendingDocumentOrganization.id);
        this.clearOrganizationDocumentsLocally(this.pendingDocumentOrganization.id);
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Organization documents deleted successfully',
        });
        this.closeDeleteDocumentsModal(true);
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to delete organization documents',
        });
      } finally {
        this.isDeletingDocuments = false;
      }
    },
    async confirmNotifyOrganization() {
      if (!this.pendingNotifyOrganization?.id) {
        return;
      }

      const recipientId = this.pendingNotifyOrganization.user?.id;

      if (!recipientId) {
        this.$toast.show({
          type: 'ERROR',
          message: 'This organization does not have a linked user to notify',
        });
        return;
      }

      this.isNotifying = true;

      const organizationName = this.pendingNotifyOrganization.name || 'Your organization';
      const payload = {
        recipient: Number(recipientId),
        title: 'Organization Renewal Reminder',
        message: `${organizationName} is currently inactive. Please renew your organization subscription to restore access.`,
        is_global: false,
        is_read: false,
      };

      try {
        await createNotification(payload);
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Renewal notification sent successfully',
        });
        this.closeNotifyModal(true);
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to notify organization',
        });
      } finally {
        this.isNotifying = false;
      }
    },
    toggleDropdownById(id) {
      this.organizations.forEach((organization) => {
        // eslint-disable-next-line no-param-reassign
        organization.showDrop = organization.id === id;
      });
    },
    closeDropdown() {
      this.organizations.forEach((organization) => {
        // eslint-disable-next-line no-param-reassign
        organization.showDrop = false;
      });
    },
  },
  mounted() {
    const from = localStorage.getItem('from');
    const requestedTab = this.$route?.query?.tab;
    if (['Incomplete', 'Pending', 'Approved'].includes(requestedTab)) {
      this.activeTab = requestedTab;
    } else if (from === 'ApprovedDetailsPage') {
      this.activeTab = 'Approved';
    }
    this.getOrganization();
    this.getPrograms();
  },
};
</script>

<style scoped>
.organizations-page {
  padding: 1.75rem 1rem 2.5rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.12), transparent 24%),
    linear-gradient(180deg, #f5faf6 0%, #eff5f0 100%);
}

.organizations-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(10, 33, 17, 0.96), rgba(23, 72, 36, 0.88)),
    linear-gradient(140deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 24px 80px rgba(17, 41, 24, 0.16);
  color: #f7fff7;
}

.hero-copy {
  max-width: 45rem;
}

.hero-eyebrow,
.section-kicker,
.filters-label,
.table-kicker {
  margin-bottom: 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-eyebrow {
  color: rgba(247, 255, 247, 0.72);
}

.organizations-hero h1 {
  margin-bottom: 0.75rem;
  font-family: 'Open Sans', sans-serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.12;
}

.hero-description {
  margin: 0;
  max-width: 40rem;
  color: rgba(247, 255, 247, 0.8);
  line-height: 1.75;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(140px, 1fr));
  gap: 1rem;
  min-width: 18rem;
}

.stat-card {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.stat-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: rgba(247, 255, 247, 0.72);
}

.stat-value {
  font-size: 1.2rem;
}

.admin-nav {
  margin-bottom: 1rem;
}

.admin-nav .nav {
  gap: 0.6rem;
  padding: 0;
}

.admin-nav ul li {
  list-style: none;
}

.admin-nav ul li a {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  color: #40604a;
  font-size: 0.82rem;
  text-decoration: none;
}

.organization-wrapper {
  padding: 1.6rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 18px 60px rgba(22, 50, 28, 0.08);
  backdrop-filter: blur(10px);
}

.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.section-kicker,
.filters-label,
.table-kicker {
  color: #2c8d41;
}

.section-heading h2,
.filters-panel h3,
.table-shell__header h3 {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  color: #132218;
}

.tab-switcher {
  display: inline-flex;
  gap: 0.85rem;
  padding: 0.45rem;
  margin-bottom: 1.5rem;
  border-radius: 22px;
  background: #edf4ee;
}

.tab-button {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.95rem 1.2rem;
  border: 0;
  border-radius: 18px;
  background: transparent;
  color: #33503c;
  transition: all 0.2s ease;
}

.tab-button.active {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 14px 30px rgba(48, 171, 76, 0.22);
  color: #fff;
}

.tab-title {
  font-weight: 700;
}

.tab-count {
  min-width: 2rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 0.78rem;
  text-align: center;
}

.filters-panel {
  padding: 1.4rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.96), rgba(241, 247, 242, 0.96));
}

.filters-intro {
  margin-bottom: 1rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
  gap: 1rem;
  align-items: end;
}

.search-shell {
  position: relative;
}

.search-shell i {
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  color: #6b7a72;
}

.search_input {
  width: 100%;
  min-height: 3.35rem;
  padding: 0.9rem 1rem 0.9rem 2.8rem;
  border: 1px solid rgba(28, 68, 38, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
}

.search_input:focus {
  outline: none;
  border-color: rgba(48, 171, 76, 0.6);
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.1);
}

.select-shell ::v-deep > div {
  width: 100% !important;
}

.select-shell ::v-deep .form-group {
  margin: 0 !important;
}

.select-shell ::v-deep .form-group > label {
  margin-bottom: 0.45rem;
  color: #24392a !important;
  font-size: 0.88rem;
}

.select-shell ::v-deep select {
  min-height: 3.35rem !important;
  border: 1px solid rgba(28, 68, 38, 0.12) !important;
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.92) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
  color: #223429 !important;
  padding: 0.9rem 1rem !important;
}

.select-shell ::v-deep select:focus {
  border-color: rgba(48, 171, 76, 0.6) !important;
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.1) !important;
}

.feedback-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 18rem;
  padding: 2rem;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(250, 252, 250, 0.92), rgba(239, 245, 240, 0.92));
  text-align: center;
}

.feedback-state--empty h3 {
  margin-bottom: 0.4rem;
  font-family: 'Open Sans', sans-serif;
  color: #132218;
}

.feedback-state p {
  max-width: 28rem;
  margin: 0.4rem 0 0;
  color: #5f6f66;
}

.table-shell {
  padding: 1.2rem;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(250, 252, 250, 0.95), rgba(239, 245, 240, 0.95));
  overflow: hidden;
}

.table-shell__header {
  margin-bottom: 1rem;
}

.premium-table {
  margin-bottom: 0;
}

.table-scroll-hint {
  display: none;
  margin-bottom: 0.75rem;
  color: #5f7267;
  font-size: 0.8rem;
  font-weight: 600;
}

.table-scroll-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  border-radius: 18px;
  scrollbar-width: thin;
  scrollbar-color: rgba(34, 111, 57, 0.45) rgba(237, 244, 238, 0.9);
}

.table-scroll-shell::-webkit-scrollbar {
  height: 9px;
}

.table-scroll-shell::-webkit-scrollbar-track {
  background: rgba(237, 244, 238, 0.9);
  border-radius: 999px;
}

.table-scroll-shell::-webkit-scrollbar-thumb {
  background: rgba(34, 111, 57, 0.45);
  border-radius: 999px;
}

.premium-table ::v-deep table {
  background: transparent;
  min-width: 860px;
  table-layout: auto;
}

.premium-table ::v-deep thead th {
  padding: 1rem 0.85rem;
  border-top: 0;
  border-bottom: 1px solid rgba(28, 68, 38, 0.08);
  background: rgba(48, 171, 76, 0.1);
  white-space: nowrap;
}

.premium-table ::v-deep tbody td {
  padding: 1rem 0.85rem;
  vertical-align: middle;
  border-top: 1px solid rgba(28, 68, 38, 0.06);
  background: rgba(255, 255, 255, 0.76);
}

.table-header-style {
  font-size: 0.84rem;
  font-weight: 700;
  color: #1d3323;
  white-space: nowrap;
}

.cell-name {
  display: block;
  min-width: 170px;
  max-width: 250px;
  word-wrap: break-word;
  white-space: normal;
  font-weight: 600;
  color: #203026;
}

.premium-table ::v-deep th:nth-child(3),
.premium-table ::v-deep td:nth-child(3) {
  min-width: 110px;
}

.premium-table ::v-deep th:nth-child(4),
.premium-table ::v-deep td:nth-child(4) {
  min-width: 150px;
}

.premium-table ::v-deep th:nth-child(5),
.premium-table ::v-deep td:nth-child(5) {
  min-width: 190px;
}

.premium-table ::v-deep th:nth-child(6),
.premium-table ::v-deep td:nth-child(6) {
  min-width: 140px;
}

.email-cell {
  display: block;
  width: 11rem;
  max-width: 11rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.focus-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.focus-badge {
  padding: 0.45rem 0.7rem;
  border: 1px solid rgba(48, 171, 76, 0.14);
  border-radius: 999px;
  background: #edf7ef !important;
  color: #256536 !important;
  font-weight: 600;
}

.index-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  min-height: 2rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-weight: 700;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 5.5rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: capitalize;
}

.status-badge--active {
  background: #e8f5eb;
  color: #217136;
}

.status-badge--inactive {
  background: #fdecec;
  color: #bb4e4e;
}

.status-badge--neutral {
  background: #eef2f0;
  color: #5a6c61;
}

.table-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 0;
  border-radius: 12px;
  background: #edf7ef;
  color: #256536;
}

.table-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 0.95rem;
  border: 0;
  border-radius: 12px;
  background: #edf7ef;
  color: #256536;
  font-size: 0.82rem;
  font-weight: 700;
  transition: background 0.2s ease, transform 0.2s ease;
}

.table-link-btn:hover {
  background: #dff1e4;
  transform: translateY(-1px);
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1rem;
}

.pagination-meta {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.pagination-meta p,
.pagination-range {
  margin: 0;
  color: #4e6156;
}

.rows-select {
  min-width: 4.5rem;
  height: 2.6rem;
  padding: 0 0.8rem;
  border: 1px solid rgba(28, 68, 38, 0.12);
  border-radius: 12px;
  background: #fff;
}

.pagination-shell {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

.premium-pagination {
  margin: 0;
}

.premium-pagination ::v-deep .pagination {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin: 0;
  padding: 0.45rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72), 0 14px 30px rgba(18, 42, 24, 0.06);
}

.premium-pagination ::v-deep .page-item {
  margin: 0;
}

.premium-pagination ::v-deep .page-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  height: 2.5rem;
  padding: 0 0.85rem;
  border: 0;
  border-radius: 12px !important;
  background: transparent;
  color: #294333;
  font-size: 0.82rem;
  font-weight: 700;
  box-shadow: none !important;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.premium-pagination ::v-deep .page-item:not(.active):not(.disabled) .page-link:hover,
.premium-pagination ::v-deep .page-item:not(.active):not(.disabled) .page-link:focus {
  background: #edf7ef;
  color: #256536;
  transform: translateY(-1px);
}

.premium-pagination ::v-deep .page-item.active .page-link {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 12px 24px rgba(48, 171, 76, 0.22) !important;
  color: #fff;
}

.premium-pagination ::v-deep .page-item.disabled .page-link {
  background: transparent;
  color: #9daf9f;
  opacity: 0.75;
}

.pagination-nav-label {
  letter-spacing: 0.01em;
}

.load-icon {
  width: 4rem;
  height: 4rem;
  color: #2da047;
}

.drop-wrap {
  position: relative;
}

.drop-body {
  position: absolute;
  right: 0;
  bottom: -7.6rem;
  width: 13rem;
  padding: 0.8rem;
  border: 1px solid rgba(28, 68, 38, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 16px 36px rgba(16, 34, 21, 0.12);
  z-index: 100;
}

.drop-body p {
  margin: 0;
  padding: 0.55rem 0.45rem;
  border-radius: 10px;
  font-size: 0.84rem;
  cursor: pointer;
}

.drop-body p:hover {
  color: #1e6930;
  background: #eef7f0;
}

.empty-table-text {
  padding: 2rem 0;
  text-align: center;
  color: #2d7c40;
}

@media (max-width: 991px) {
  .organizations-page {
    padding-top: 2rem;
  }

  .organizations-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-stats {
    width: 100%;
    min-width: 0;
  }

  .filters-grid,
  .table-footer {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .tab-switcher {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .tab-button {
    width: 100%;
    justify-content: space-between;
    min-width: 0;
  }

  .tab-title {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .pagination-shell {
    justify-content: flex-start;
  }
}

@media (max-width: 767px) {
  .organizations-page {
    padding: 1.5rem 0.5rem 2rem;
  }

  .organizations-hero,
  .organization-wrapper {
    padding: 1.2rem;
    border-radius: 22px;
  }

  .tab-switcher {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }

  .admin-nav .nav {
    flex-wrap: wrap;
  }

  .premium-pagination ::v-deep .pagination {
    width: max-content;
    flex-wrap: nowrap;
    justify-content: flex-start;
  }

  .table-scroll-hint {
    display: block;
  }

  .table-shell {
    padding: 1rem;
  }

  .premium-table ::v-deep table {
    min-width: 720px;
  }

  .premium-table ::v-deep thead th,
  .premium-table ::v-deep tbody td {
    padding: 0.85rem 0.7rem;
  }

  .pagination-meta,
  .pagination-range {
    width: 100%;
  }

  .pagination-shell {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0.2rem;
  }
}

@media (max-width: 600px) {
  .organizations-page {
    padding-top: 0.85rem;
  }

  .tab-button {
    padding: 0.85rem 1rem;
  }

  .tab-title {
    font-size: 0.92rem;
  }

  .premium-table ::v-deep table {
    min-width: 680px;
  }

  .email-cell {
    width: 9rem;
    max-width: 9rem;
  }
}
</style>
